import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { MapPin, LogOut, User, Package, ShieldCheck, Menu, X, Heart, Home as HomeIcon, Sun, Moon, Monitor, ChevronDown, Phone, FolderTree } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { NAV } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useTheme } from "@/context/ThemeContext";
import { api } from "@/lib/api";

const THEME_OPTIONS = [
  { key: "light", label: "Light", icon: Sun },
  { key: "dark", label: "Dark", icon: Moon },
  { key: "system", label: "System", icon: Monitor },
];

function ThemeSwitcher({ compact = false }) {
  const { pref, resolved, setPreference } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="theme-switcher-btn"
          title={`Theme: ${pref === "default" ? "Store default" : pref}`}
          className="p-2 rounded-full border border-hyd-border text-hyd-text2 hover:text-hyd-gold hover:border-hyd-gold/60 transition-colors"
        >
          {resolved === "light" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-hyd-panel border-hyd-border min-w-[150px]">
        {THEME_OPTIONS.map((o) => (
          <DropdownMenuItem
            key={o.key}
            data-testid={`theme-option-${o.key}`}
            onClick={() => setPreference(o.key)}
            className={`flex items-center gap-2 text-sm cursor-pointer ${pref === o.key ? "text-hyd-gold" : "text-hyd-text2"}`}
          >
            <o.icon className="w-4 h-4" /> {o.label}
            {pref === o.key && <span className="ml-auto text-[9px] uppercase font-bold">On</span>}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem
          data-testid="theme-option-default"
          onClick={() => setPreference(null)}
          className={`flex items-center gap-2 text-sm cursor-pointer border-t border-hyd-border mt-1 pt-2 ${pref === "default" ? "text-hyd-gold" : "text-hyd-text2"}`}
        >
          <ShieldCheck className="w-4 h-4" /> Store default
          {pref === "default" && <span className="ml-auto text-[9px] uppercase font-bold">On</span>}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileThemeRow() {
  const { pref, setPreference } = useTheme();
  return (
    <div className="flex gap-1 bg-hyd-panel border border-hyd-border rounded-full p-1">
      {THEME_OPTIONS.map((o) => (
        <button
          key={o.key}
          data-testid={`theme-option-mobile-${o.key}`}
          onClick={() => setPreference(o.key)}
          className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold ${pref === o.key ? "bg-hyd-gold text-hyd-bg" : "text-hyd-text2"}`}
        >
          <o.icon className="w-3 h-3" /> {o.label}
        </button>
      ))}
    </div>
  );
}

export function Header({ onOpenPincode }) {
  const { user, logout } = useAuth();
  const { totals, pincode } = useCart();
  const { count: wishCount } = useWishlist();
  const [menuOpen, setMenuOpen] = useState(false);
  const [tree, setTree] = useState([]);
  const [openGroup, setOpenGroup] = useState(null); // mobile accordion
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/categories/tree").then((r) => setTree(r.data || [])).catch(() => {});
  }, []);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-hyd-bg/80 border-b border-hyd-border">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
        <Logo />

        {/* Pincode chip */}
        <button
          data-testid={NAV.pincodeChip}
          onClick={onOpenPincode}
          className="ml-auto sm:ml-4 flex items-center gap-1.5 text-xs sm:text-sm text-hyd-text2 hover:text-hyd-gold transition-colors px-2.5 py-1.5 rounded-full border border-hyd-border"
        >
          <MapPin className="w-3.5 h-3.5" />
          <span className="hidden xs:inline">{pincode ? `Deliver to ${pincode}` : "Set pincode"}</span>
          <span className="xs:hidden">{pincode || "Pincode"}</span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1 text-sm">
          <Link to="/" data-testid={NAV.home} className="px-3 py-1.5 rounded-md hover:bg-hyd-panel text-hyd-text2 hover:text-hyd-gold">Home</Link>

          {/* Categories mega menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-categories" className="px-3 py-1.5 rounded-md hover:bg-hyd-panel text-hyd-text2 hover:text-hyd-gold inline-flex items-center gap-1">
                Categories <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-hyd-panel border-hyd-border min-w-[320px] p-2">
              {tree.length === 0 && <div className="px-3 py-2 text-xs text-hyd-text3">Loading…</div>}
              {tree.map((root) => (
                <div key={root.id} className="mb-2 last:mb-0">
                  <Link
                    to={`/category/${root.slug}`}
                    data-testid={`nav-cat-${root.slug}`}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md text-hyd-gold hover:bg-hyd-bg"
                  >
                    <FolderTree className="w-3.5 h-3.5" />
                    <span className="font-bold uppercase tracking-widest text-[11px]">{root.name}</span>
                  </Link>
                  {(root.children || []).length > 0 && (
                    <div className="pl-6 mt-1 flex flex-col">
                      {root.children.map((s) => (
                        <Link
                          key={s.id}
                          to={`/${root.slug}/${s.slug}`}
                          data-testid={`nav-subcat-${s.slug}`}
                          className="px-2 py-1 rounded-md text-xs text-hyd-text2 hover:bg-hyd-bg hover:text-hyd-text"
                        >
                          {s.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/standard_crackers_pricelist" data-testid="nav-quick-order" className="px-3 py-1.5 rounded-md hover:bg-hyd-panel text-hyd-gold flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5" /> Quick Order
          </Link>
          <Link to="/combos" data-testid={NAV.combos} className="px-3 py-1.5 rounded-md hover:bg-hyd-panel text-hyd-text2 hover:text-hyd-gold">Combos</Link>
          <Link to="/contact" data-testid="nav-contact" className="px-3 py-1.5 rounded-md hover:bg-hyd-panel text-hyd-text2 hover:text-hyd-gold inline-flex items-center gap-1">
            <Phone className="w-3.5 h-3.5" /> Contact
          </Link>
          {user && (
            <Link to="/wishlist" data-testid={NAV.wishlistBadge} className="px-3 py-1.5 rounded-md hover:bg-hyd-panel text-hyd-text2 hover:text-hyd-red flex items-center gap-1">
              <Heart className={`w-3.5 h-3.5 ${wishCount > 0 ? "fill-hyd-red text-hyd-red" : ""}`} />
              {wishCount > 0 && <span>{wishCount}</span>}
            </Link>
          )}
          {user && (
            <Link to="/orders" data-testid={NAV.orders} className="px-3 py-1.5 rounded-md hover:bg-hyd-panel text-hyd-text2 hover:text-hyd-gold">Orders</Link>
          )}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid={NAV.admin} className="px-3 py-1.5 rounded-md text-hyd-gold hover:bg-hyd-panel flex items-center gap-1">
              <ShieldCheck className="w-4 h-4" /> Admin
            </Link>
          )}
        </nav>

        {/* Auth */}
        <div className="flex items-center gap-2">
          <ThemeSwitcher />
          {user ? (
            <button
              data-testid={NAV.logoutBtn}
              onClick={() => { logout(); navigate("/"); }}
              className="hidden sm:flex items-center gap-1 text-xs text-hyd-text2 hover:text-hyd-red transition-colors px-2 py-1.5"
              title={user.phone}
            >
              <LogOut className="w-4 h-4" /> <span className="hidden md:inline">Logout</span>
            </button>
          ) : (
            <Link
              to="/login"
              data-testid={NAV.loginBtn}
              className="text-xs sm:text-sm font-bold uppercase tracking-wider bg-hyd-gold text-hyd-bg px-3 py-1.5 rounded-full hover:bg-hyd-goldDim transition-colors flex items-center gap-1"
            >
              <User className="w-3.5 h-3.5" /> Login
            </Link>
          )}
          {totals.count > 0 && (
            <Link
              to="/cart"
              data-testid={NAV.cartBadge}
              className="relative p-2 rounded-full bg-hyd-panel border border-hyd-border hover:border-hyd-gold"
            >
              <Package className="w-4 h-4 text-hyd-gold" />
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-hyd-red text-white text-[10px] font-bold grid place-items-center">
                {totals.count}
              </span>
            </Link>
          )}
          <button className="md:hidden p-2 text-hyd-text2" onClick={() => setMenuOpen((v) => !v)}>
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-hyd-border bg-hyd-bg px-4 py-3 flex flex-col gap-1 text-sm">
          <div className="flex items-center gap-2 py-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-hyd-text3">Theme</span>
            <MobileThemeRow />
          </div>
          <Link onClick={() => setMenuOpen(false)} to="/" className="py-2 text-hyd-text2 hover:text-hyd-gold">Home</Link>
          <Link
            onClick={() => setMenuOpen(false)}
            to="/standard_crackers_pricelist"
            data-testid="nav-quick-order-mobile"
            className="my-1 flex items-center gap-2 rounded-lg bg-hyd-gold px-3 py-2.5 font-bold text-hyd-bg hover:bg-hyd-goldDim"
          >
            <ShieldCheck className="w-4 h-4" /> Quick Order
          </Link>
          {user && (
            <Link onClick={() => setMenuOpen(false)} to="/orders" data-testid={NAV.orders} className="py-2 text-hyd-text2 hover:text-hyd-gold">
              My Orders
            </Link>
          )}

          {/* Categories accordion */}
          <div className="border-t border-hyd-border mt-1 pt-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-hyd-text3 py-2">Categories</div>
            {tree.map((root) => (
              <div key={root.id} className="mb-1">
                <button
                  type="button"
                  data-testid={`mobile-cat-toggle-${root.slug}`}
                  onClick={() => setOpenGroup((g) => (g === root.id ? null : root.id))}
                  className="w-full flex items-center justify-between py-2 text-hyd-text font-bold"
                >
                  <span className="flex items-center gap-2"><FolderTree className="w-3.5 h-3.5 text-hyd-gold" /> {root.name}</span>
                  <ChevronDown className={`w-4 h-4 text-hyd-text3 transition-transform ${openGroup === root.id ? "rotate-180" : ""}`} />
                </button>
                {openGroup === root.id && (
                  <div className="pl-6 flex flex-col gap-0.5 pb-1">
                    <Link
                      onClick={() => setMenuOpen(false)}
                      to={`/category/${root.slug}`}
                      data-testid={`mobile-cat-link-${root.slug}`}
                      className="py-1.5 text-xs text-hyd-gold font-bold"
                    >
                      View all {root.name}
                    </Link>
                    {(root.children || []).map((s) => (
                      <Link
                        key={s.id}
                        onClick={() => setMenuOpen(false)}
                        to={`/${root.slug}/${s.slug}`}
                        data-testid={`mobile-subcat-link-${s.slug}`}
                        className="py-1.5 text-xs text-hyd-text2 hover:text-hyd-gold"
                      >
                        {s.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <Link onClick={() => setMenuOpen(false)} to="/combos" className="py-2 text-hyd-text2 hover:text-hyd-gold border-t border-hyd-border mt-1 pt-3">Combos</Link>
          <Link onClick={() => setMenuOpen(false)} to="/contact" data-testid="nav-contact-mobile" className="py-2 text-hyd-text2 hover:text-hyd-gold inline-flex items-center gap-2">
            <Phone className="w-3.5 h-3.5" /> Contact info
          </Link>

          {user && <Link onClick={() => setMenuOpen(false)} to="/wishlist" className="py-2 text-hyd-text2 hover:text-hyd-red border-t border-hyd-border mt-1 pt-3">Wishlist {wishCount > 0 ? `(${wishCount})` : ""}</Link>}
          {user && <Link onClick={() => setMenuOpen(false)} to="/account/addresses" className="py-2 text-hyd-text2 hover:text-hyd-gold inline-flex items-center gap-2"><HomeIcon className="w-3.5 h-3.5" /> Addresses</Link>}
          {user?.role === "admin" && <Link onClick={() => setMenuOpen(false)} to="/admin" className="py-2 text-hyd-gold">Admin</Link>}
          {user && (
            <button onClick={() => { logout(); setMenuOpen(false); navigate("/"); }} className="py-2 text-left text-hyd-red border-t border-hyd-border mt-1 pt-3">
              Logout
            </button>
          )}
        </div>
      )}
    </header>
  );
}
